import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  NzTableFilterFn,
  NzTableFilterList,
  NzTableModule,
  NzTableQueryParams,
  NzTableSortFn,
  NzTableSortOrder,
} from 'ng-zorro-antd/table';
import { NgForOf, NgIf } from '@angular/common';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { filter, firstValueFrom, map, Observable } from 'rxjs';
import { StoreService } from '../../store/store.service';
import { toLower } from 'lodash';
import { Clothing } from '../../api/clothing-api.class';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { MainButtonComponent } from '../../shared/ui/main-button/main-button.component';
import { NzSpinComponent } from 'ng-zorro-antd/spin';

interface ColumnItem {
  id: number;
  name: string;
  sortOrder: NzTableSortOrder | null;
  sortFn: NzTableSortFn<Clothing> | null;
  listOfFilter: NzTableFilterList;
  filterFn: NzTableFilterFn<Clothing> | null;
}

@Component({
  selector: 'app-clothing-list',
  standalone: true,
  imports: [
    NzTableModule,
    NgForOf,
    NzButtonComponent,
    NzIconDirective,
    NzInputDirective,
    NzInputGroupComponent,
    ReactiveFormsModule,
    FormsModule,
    NzDropDownModule,
    MainButtonComponent,
    NgIf,
    NzSpinComponent,
  ],
  templateUrl: './clothing-list.component.html',
  styleUrl: './clothing-list.component.scss',
})
export class ClothingListComponent implements OnInit, AfterViewInit {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  loading = false;
  searchSizeVisible = false;
  selectedClothing: number | undefined;
  displayedClothing: Clothing[] = [];
  clothing$: Observable<Clothing[]>;
  searchValue = '';
  searchSizeValue = '';
  listOfColumns: ColumnItem[] = [
    {
      id: 0,
      name: 'Barcode /QR-Code',
      sortOrder: null,
      sortFn: (a: Clothing, b: Clothing) =>
        a.bezeichnung.localeCompare(b.inventarnummer),
      listOfFilter: [],
      filterFn: null,
    },
    {
      id: 1,
      name: 'Bezeichnung',
      sortOrder: null,
      sortFn: (a: Clothing, b: Clothing) =>
        a.bezeichnung.localeCompare(b.bezeichnung),
      listOfFilter: [
        { text: 'Arbeitsschuhe/Stiefel', value: 1 },
        { text: 'Kittel', value: 2 },
        { text: 'Hosen', value: 3 },
        { text: 'Jacken', value: 4 },
        { text: 'Hemden', value: 5 },
      ],
      filterFn: (list: number[], item: Clothing) =>
        list.some((status) => item.kleidungstyp === status),
    },
    {
      id: 2,
      name: 'Status',
      sortFn: (a: Clothing, b: Clothing) =>
        a.statusText.localeCompare(b.statusText),
      sortOrder: null,
      listOfFilter: [
        { text: 'Frei', value: 'frei' },
        { text: 'Ausgeliehen', value: 'ausgeliehen' },
        { text: 'Dauerleihe', value: 'dauerleihe' },
        { text: 'Beschädigt', value: 'beschädigt' },
        { text: 'Waschen', value: 'waschen' },
      ],
      filterFn: (list: string[], item: Clothing) =>
        list.some((status) => item.statusText === status),
    },
  ];
  tableState$ = this.store.select((state) => state.tables.ClothingListTable);
  private isRestoringTableState = false;

  constructor(
    private router: Router,
    private store: StoreService,
  ) {
    this.clothing$ = this.store
      .select((state) => state.clothing.activeClothingList)
      .pipe(
        filter((c) => !!c),
        map((c) => Clothing.fromResultArray(c)),
      );
  }

  async ngOnInit() {
    this.isRestoringTableState = true;
    this.loading = true;

    const tableState = await firstValueFrom(this.tableState$);

    if (tableState) {
      const {
        tables: { applyFiltersToColumns, applySortToColumns },
      } = this.store.getActions();

      applyFiltersToColumns(tableState, this.listOfColumns);
      applySortToColumns(tableState, this.listOfColumns);
    }

    if (tableState?.searchFields?.['kleidergroesse']) {
      this.searchSizeValue = tableState.searchFields['kleidergroesse'];
    }

    const {
      clothing: { setActiveClothingList },
    } = this.store.getActions();
    await setActiveClothingList();
    const clothing = await firstValueFrom(this.clothing$);
    this.displayedClothing = this.searchSizeValue
      ? clothing.filter((c) =>
          toLower(c.kleidergroesseText).includes(
            toLower(this.searchSizeValue),
          ),
        )
      : clothing;
    this.isRestoringTableState = false;
    this.loading = false;
  }

  ngAfterViewInit() {
    this.searchInput.nativeElement.focus();
  }

  onTableQueryParamsChange(params: NzTableQueryParams) {
    // Guard: verhindern, dass TableQueryParams vor Init den Store überschreiben
    if (this.isRestoringTableState) {
      return;
    }

    const {
      tables: { buildTableStateFromQueryParams },
    } = this.store.getActions();

    const tableState = buildTableStateFromQueryParams(
      params,
      this.listOfColumns,
    );

    const {
      tables: { setClothingTableState },
    } = this.store.getActions();
    setClothingTableState({
      ...tableState,
      searchFields: { kleidergroesse: this.searchSizeValue },
    });
  }

  onClothingClick(data: Clothing) {
    this.selectedClothing = data.id;
    this.router.navigate(['clothing', this.selectedClothing]);
  }

  navigateToNew() {
    this.router.navigate(['clothing', 'new-clothing']);
  }

  async onSearch() {
    const clothing = await firstValueFrom(this.clothing$);

    this.displayedClothing =
      this.searchValue.length > 0
        ? clothing.filter((c) =>
            toLower(c.inventarnummer).includes(toLower(this.searchValue)),
          )
        : clothing;
  }

  async resetSize() {
    this.searchSizeValue = '';
    await this.searchSize();
  }

  async searchSize() {
    this.searchSizeVisible = false;
    const clothing = await firstValueFrom(this.clothing$);

    this.displayedClothing =
      this.searchSizeValue.length > 0
        ? clothing.filter((c) =>
            toLower(c.kleidergroesseText).includes(
              toLower(this.searchSizeValue),
            ),
          )
        : clothing;

    this.persistSearchSizeValue();
  }

  private async persistSearchSizeValue() {
    const {
      tables: { setClothingTableState },
    } = this.store.getActions();

    const currentState = await firstValueFrom(this.tableState$);
    setClothingTableState({
      sort: currentState?.sort,
      filters: currentState?.filters ?? {},
      searchFields: {
        ...currentState?.searchFields,
        kleidergroesse: this.searchSizeValue,
      },
    });
  }
}
